/**
 * Client for the AI usage / model / pricing admin API (VA-27555).
 *
 * Reuses `voiceAdminClient`, so it carries the same `X-Voice-Admin-Token` and
 * inherits its 401 → force-relogin behaviour. These endpoints replace the
 * `/api/admin/metering/*` set the previous console called, which no longer
 * exists on the backend.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voiceAdminClient } from '../voice-admin-api';
/**
 * Selects the records that carry NO organisation.
 *
 * "No filter" and "only the ones with nothing" are different queries that both
 * want to say the organisation is absent, so the unattributed bucket needs a
 * value of its own. Using the empty string would collide with the picker's "all
 * organizations" option and render both as selected.
 */
export const UNATTRIBUTED = '__unattributed__';
/**
 * Spend reports: reuse a result for a minute instead of refetching on every tab
 * switch and every return to the window. Usage changes slowly and each report
 * is a full aggregation; a mutation still invalidates what it changes.
 */
const REPORT_QUERY = { staleTime: 60000, refetchOnWindowFocus: false };
const qs = (params) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '')
            search.set(k, String(v));
    });
    const s = search.toString();
    return s ? `?${s}` : '';
};
// ── queries ───────────────────────────────────────────────────────────────
export const useUsageMeta = () => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { queryKey: ['ai-usage', 'meta'], queryFn: () => voiceAdminClient.get('/admin/ai/meta/'), staleTime: 5 * 60 * 1000 }));
export const useModels = (params = {}) => useQuery({
    queryKey: ['ai-usage', 'models', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/models/${qs(params)}`),
});
/** The type-filtered choice list for one feature — what a dropdown renders. */
export const useModelChoices = (feature) => useQuery({
    enabled: !!feature,
    queryKey: ['ai-usage', 'model-choices', feature],
    queryFn: () => voiceAdminClient.get(`/admin/ai/models/${qs({ feature: feature })}`),
});
export const useAssignments = (environment = 'default') => useQuery({
    queryKey: ['ai-usage', 'assignments', environment],
    queryFn: () => voiceAdminClient.get(`/admin/ai/assignments/${qs({ environment })}`),
});
export const usePricing = (params = {}) => useQuery({
    queryKey: ['ai-usage', 'pricing', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/pricing/${qs(params)}`),
});
/** `enabled` lets a screen skip a query it does not render — the usage
 *  sub-sections each show one thing, and fetching all three every time would
 *  triple the requests for data two of them throw away. */
export const useUsageSummary = (params, enabled = true) => useQuery({
    enabled,
    queryKey: ['ai-usage', 'summary', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/summary/${qs(params)}`),
});
export const useUsageRecords = (params) => useQuery({
    queryKey: ['ai-usage', 'records', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/records/${qs(params)}`),
});
/** Usage events folded into the API calls they came from — one row per call. */
export const useUsageOperations = (params, enabled = true) => useQuery({
    enabled,
    queryKey: ['ai-usage', 'operations', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/operations/${qs(params)}`),
});
export const useUsageTimeseries = (params) => useQuery({
    queryKey: ['ai-usage', 'timeseries', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/timeseries/${qs(params)}`),
});
export const useInterviewEconomics = (params = {}, enabled = true) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled, queryKey: ['ai-usage', 'interview-economics', params], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/interview-economics/${qs(params)}`) }));
/**
 * Every organisation's spend, grouped, with each one's lifecycle mix.
 *
 * The superadmin landing query. Doubles as the source for the organisation
 * picker: the list comes from the usage records themselves, so the filter can
 * never offer an organisation that returns nothing.
 */
export const useOrganizations = (params = {}) => useQuery({
    queryKey: ['ai-usage', 'organizations', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/organizations/${qs(params)}`),
});
/**
 * What one phase's number consists of, under the caller's current scope.
 *
 * `params` must carry the same filters the card was shown under — otherwise
 * opening a card inside a filtered view answers a different question from the
 * one on screen.
 */
export const usePhaseDetail = (phase, params = {}) => useQuery({
    enabled: !!phase,
    queryKey: ['ai-usage', 'phase', phase, params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/phases/${encodeURIComponent(phase)}/${qs(params)}`),
});
/** One organisation: its phases, its interviews, its recruiters, its trend. */
export const useOrganizationDetail = (organizationId, params = {}) => useQuery({
    enabled: !!organizationId,
    queryKey: ['ai-usage', 'organization', organizationId, params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/organizations/${encodeURIComponent(organizationId)}/${qs(params)}`),
});
/** The admin dashboard: period, organisation and mode are its only filters. */
export const useUsageOverview = (params) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { queryKey: ['ai-usage', 'overview', params], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/overview/${qs(params)}`), placeholderData: (prev) => prev }));
export const useUsageBreakdown = (params, enabled = true) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled, queryKey: ['ai-usage', 'breakdown', params], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/breakdown/${qs(params)}`), placeholderData: (prev) => prev }));
export const useUsageInterviews = (params, enabled = true) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled, queryKey: ['ai-usage', 'interviews', params], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/interviews/${qs(params)}`), placeholderData: (prev) => prev }));
export const useUsageCalls = (params, enabled = true) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled, queryKey: ['ai-usage', 'calls', params], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/calls/${qs(params)}`), placeholderData: (prev) => prev }));
export const useUsageDashboard = (params, enabled = true) => useQuery({
    enabled,
    queryKey: ['ai-usage', 'dashboard', params],
    queryFn: () => voiceAdminClient.get(`/admin/ai/usage/dashboard/${qs(params)}`),
});
/** The full cost story for one interview — every cut of "where did it go". */
export const useInterviewBreakdown = (slug) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled: !!slug, queryKey: ['ai-usage', 'interview-breakdown', slug], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/interviews/${slug}/`) }));
export const useUsageRecord = (usageId) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled: !!usageId, queryKey: ['ai-usage', 'record', usageId], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/records/${usageId}/`) }));
export const useInterviewUsage = (slug) => useQuery(Object.assign(Object.assign({}, REPORT_QUERY), { enabled: !!slug, queryKey: ['ai-usage', 'interview', slug], queryFn: () => voiceAdminClient.get(`/admin/ai/usage/interviews/${slug}/`) }));
// ── mutations ─────────────────────────────────────────────────────────────
export const useSetModelActive = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/ai/models/', body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage'] }),
    });
};
/** Create or update a model — the endpoint upserts on (provider, model_id). */
export const useSaveModel = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/ai/models/', body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage'] }),
    });
};
/**
 * Delete a model. Refused with 409 while a feature is still assigned to it —
 * the caller should surface which features, not just "failed".
 */
export const useDeleteModel = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params) => {
            var _a;
            return voiceAdminClient.delete(`/admin/ai/models/${qs({ provider: (_a = params.provider) !== null && _a !== void 0 ? _a : 'openai', model_id: params.model_id })}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage'] }),
    });
};
export const useAssignModel = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/ai/assignments/', body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage'] }),
    });
};
export const useUnassignModel = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (feature) => voiceAdminClient.delete(`/admin/ai/assignments/${qs({ feature })}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage'] }),
    });
};
export const usePublishRate = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/ai/pricing/', body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-usage', 'pricing'] }),
    });
};
